import React, { useCallback, useContext, useMemo } from 'react';
import styled from 'styled-components';

import Dropdown from 'components/Inputs/Dropdown';
import Tooltip from 'components/Tooltip';
import { BonusOverviewContext } from 'components/DriverAX/providers/BonusOverviewProvider';
import useCanOverrideCategory from 'components/DriverAX/hooks/useCanOverrideCategory';
import getRewardUnits from 'components/DriverAX/utils/getRewardUnits';
import useRewardOverrideDropdown from 'components/DriverAX/components/useRewardOverrideDropdown';

// Muted 12px dotted-underline label matching the reward heading's static rate subtitle — used
// only in the non-interactive fallback (disabled / no options). The interactive state uses the
// standard bordered dropdown so it reads as a control.
const RateLabel = styled.span<{ isMuted?: boolean }>`
  font-family: Inter;
  font-weight: 500;
  font-size: 12px;
  white-space: nowrap;
  text-decoration: underline dotted;
  color: ${(props) =>
    props.isMuted ? 'var(--color-text-muted)' : 'var(--color-text-secondary)'};
`;

/**
 * Reward-line payout override control, rendered in place of the reward's rate subtitle (the
 * "$0.01 / mile" line under the pay figure) when in override mode for an overridable line.
 * It's a minimal dropdown: the collapsed label comes from the backend-active rate/amount option
 * unless the user has staged a different selection. Selecting stages a reward override (applied
 * via the unified PUT /overrides on confirm); a persisted override is reverted by choosing
 * Baseline. When the user can't override (disqualified / not board owner) it falls back to the
 * plain rate label.
 */
const RewardOverrideControl = ({
  rewardName,
  parentRewardName,
  label,
}: {
  rewardName: string;
  // For a nested sub-reward (e.g. Fuel's mpg/idle), the aggregated parent's name. Used only as a
  // permission fallback: a sign-off board's `categories` typically lists the parent (Fuel), not
  // the individual components, so a board owner who covers the parent may override the children.
  parentRewardName?: string;
  label?: string;
}) => {
  const { rewardOverrideOptions, bonusOverview, isLoadingPreview } =
    useContext(BonusOverviewContext);

  const rewardType = bonusOverview?.bonusProgram.rewardType ?? 'monetary';

  const { canOverride: canOverrideChild } = useCanOverrideCategory({
    bonusProgramId: bonusOverview?.bonusProgram.id ?? '',
    category: rewardName,
  });
  const { canOverride: canOverrideParent } = useCanOverrideCategory({
    bonusProgramId: bonusOverview?.bonusProgram.id ?? '',
    category: parentRewardName ?? '',
  });
  const canOverride =
    canOverrideChild || (!!parentRewardName && canOverrideParent);

  const units = useMemo(() => {
    if (!bonusOverview) {
      return undefined;
    }

    return getRewardUnits({
      bonusProgram: bonusOverview.bonusProgram,
      rewardName,
    });
  }, [bonusOverview, rewardName]);

  const persistedOverride = useMemo(() => {
    return bonusOverview?.actualRewardOutput.rewardOverrides?.find(
      (o) => o.rewardName === rewardName,
    );
  }, [bonusOverview?.actualRewardOutput.rewardOverrides, rewardName]);

  const stagedOverride = useMemo(() => {
    return bonusOverview?.stagedOverrides?.rewardOverrides?.find(
      (o) => o.rewardName === rewardName,
    );
  }, [bonusOverview?.stagedOverrides?.rewardOverrides, rewardName]);

  const reward = useMemo((): BonusTypes.Rewards.AnyRewardOutput | undefined => {
    const rewards: BonusTypes.Rewards.AnyRewardOutput[] =
      bonusOverview?.actualRewardOutput.rewards ?? [];
    // Match a top-level reward, else a nested sub-reward (aggregated components like Fuel's
    // mpg/idle) so the option labels can still describe the child's bands.
    const topLevel = rewards.find((r) => r.name === rewardName);
    if (topLevel) {
      return topLevel;
    }
    for (const r of rewards) {
      const sub = (r.rewards ?? []).find((s) => s.name === rewardName);
      if (sub) {
        return sub;
      }
    }
    return undefined;
  }, [bonusOverview?.actualRewardOutput.rewards, rewardName]);

  const dataset = bonusOverview?.bonusProgram.dataset;

  const setStagedOverrides = bonusOverview?.setStagedOverrides;

  const stageRewardOverride = useCallback(
    (override: BonusTypes.Overrides.UserInputRewardOverride | undefined) => {
      if (!setStagedOverrides) {
        return;
      }
      setStagedOverrides((current) => {
        const others = (current?.rewardOverrides ?? []).filter(
          (o) => o.rewardName !== rewardName,
        );
        const rewardOverrides = override ? [...others, override] : others;
        if (
          !override &&
          rewardOverrides.length === 0 &&
          (current?.criteria.length ?? 0) === 0 &&
          !current?.qualifier
        ) {
          return undefined;
        }
        if (!current) {
          return { criteria: [], rewardOverrides };
        }
        return { ...current, rewardOverrides };
      });
    },
    [rewardName, setStagedOverrides],
  );

  const optionsEntry = rewardOverrideOptions?.get(rewardName);

  const { options, fallbackLabel, renderSelectedLabel } =
    useRewardOverrideDropdown({
      rewardName,
      rewardType,
      units,
      dataset,
      reward,
      optionsEntry,
      hasPersistedOverride: !!persistedOverride,
      stagedOverride,
      stageRewardOverride,
      label,
    });

  // Parent only mounts this in override mode for overridable lines; guard anyway.
  if (!bonusOverview || !optionsEntry) {
    return <RateLabel>{fallbackLabel}</RateLabel>;
  }

  const isQualified =
    bonusOverview.rewardOutput.eligibleForBonus === 'qualified';

  if (!isQualified || !canOverride) {
    return (
      <Tooltip
        content={
          !isQualified
            ? 'You cannot override as the driver is disqualified'
            : 'You cannot override as you are not the board owner'
        }
      >
        <RateLabel isMuted>{fallbackLabel}</RateLabel>
      </Tooltip>
    );
  }

  return (
    <Dropdown
      options={options}
      renderSelectedLabel={renderSelectedLabel}
      isDisabled={isLoadingPreview}
      unsetWidth
      size={'Small'}
      testId={`reward-override-${rewardName}-dropdown`}
    />
  );
};

export default RewardOverrideControl;
